import React, { useState, useEffect } from 'react';
import DeviceCard from './DeviceCard';
import DeviceListItem from './DeviceListItem';
import ProvisionDevice from './ProvisionDevice';
import FloorPlan, { DEVICE_DRAG_TYPE } from './FloorPlan';
import ScoreBadge from './ScoreBadge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faMapLocationDot } from '@fortawesome/free-solid-svg-icons';
import { setDevicePosition } from '../utils/api';
import { useToast } from '../context/ToastContext';

const planOpenKey = (roomId) => `floorPlanOpen:${roomId}`;

const readPlanOpen = (roomId) => {
        try {
                return localStorage.getItem(planOpenKey(roomId)) === '1';
        } catch {
                return false;
        }
};

const writePlanOpen = (roomId, open) => {
        try {
                if (open) localStorage.setItem(planOpenKey(roomId), '1');
                else localStorage.removeItem(planOpenKey(roomId));
        } catch {
                // Remembering the toggle is a convenience only.
        }
};

const RoomSection = ({ room, viewMode = 'cards', onMoveDevice, onDeviceUpdate, onRenameDevice, onRenameRoom, onDeleteRoom }) => {
        const [devices, setDevices] = useState(room.devices || []);
        const [showPlan, setShowPlan] = useState(() => readPlanOpen(room.id));
        const [highlight, setHighlight] = useState(null);
        const { showToast } = useToast();

        // Sync devices state with room.devices prop when it changes
        useEffect(() => {
                setDevices(room.devices || []);
        }, [room.devices]);

        const togglePlan = () => {
                writePlanOpen(room.id, !showPlan);
                setShowPlan(!showPlan);
        };

        // Show the device on the floor plan, opening the plan if it is hidden.
        const handleLocate = (deviceId) => {
                if (!showPlan) {
                        writePlanOpen(room.id, true);
                        setShowPlan(true);
                }
                setHighlight({ deviceId });
        };

        const setLocalPosition = (deviceId, x, y) => {
                setDevices(ds => ds.map(d => d.id === deviceId ? { ...d, plan_x: x, plan_y: y } : d));
        };

        // Optimistic: move the marker now, put it back if the save fails.
        const handlePositionChange = async (deviceId, x, y) => {
                const prev = devices.find(d => d.id === deviceId);
                if (!prev) return;
                setLocalPosition(deviceId, x, y);
                try {
                        const res = await setDevicePosition(deviceId, x, y);
                        if (!res.ok) throw new Error(`status ${res.status}`);
                } catch (e) {
                        setLocalPosition(deviceId, prev.plan_x ?? null, prev.plan_y ?? null);
                        showToast('error', 'Could not save the plant\'s position.');
                }
        };

        // The server takes every device off a removed plan.
        const handlePlanRemoved = () => {
                setDevices(ds => ds.map(d => ({ ...d, plan_x: null, plan_y: null })));
        };

        // While the plan is open, list rows and cards can be dragged onto it.
        const dragProps = (device) => showPlan ? {
                draggable: true,
                onDragStart: (e) => {
                        e.dataTransfer.setData(DEVICE_DRAG_TYPE, String(device.id));
                        e.dataTransfer.effectAllowed = 'move';
                },
        } : {};

        return (
                <section id={`${room.name.toLowerCase().replace(/\s/g, '-')}-section`} data-testid="room-section" data-room-id={room.id} className="mb-12">
                        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
                                <div className="flex items-baseline gap-3 mb-[10px]">
                                        <h2 data-testid="room-name" className="text-2xl font-bold text-text-strong">{room.name}</h2>
                                        <ScoreBadge score={room.score} testId="room-score" className="text-lg" />
                                </div>
                                <div className="flex items-center mb-[10px] space-x-2">
                                        <button
                                                onClick={togglePlan}
                                                data-testid="floor-plan-toggle"
                                                aria-pressed={showPlan}
                                                className={`p-2 transition-colors ${showPlan ? 'text-brand' : 'text-gray-400 hover:text-brand'}`}
                                                title={showPlan ? 'Hide floor plan' : 'Show floor plan'}
                                        >
                                                <FontAwesomeIcon icon={faMapLocationDot} className="w-4 h-4" />
                                        </button>
                                        <button
                                                onClick={() => onRenameRoom(room)}
                                                data-testid="room-rename"
                                                className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                                title="Rename room"
                                        >
                                                <FontAwesomeIcon icon={faPencil} className="w-4 h-4" />
                                        </button>
                                        <button
                                                onClick={() => onDeleteRoom(room)}
                                                data-testid="room-delete"
                                                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                                title="Delete room"
                                        >
                                                <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                                        </button>
                                        <ProvisionDevice roomId={room.id} />
                                </div>
                        </div>

                        {showPlan && (
                                <FloorPlan
                                        room={room}
                                        devices={devices}
                                        highlight={highlight}
                                        onPositionChange={handlePositionChange}
                                        onPlanRemoved={handlePlanRemoved}
                                />
                        )}

                        {viewMode === 'list' ? (
                                <div
                                        className="grid gap-y-2"
                                        style={{ gridTemplateColumns: 'auto auto auto 1fr auto auto' }}
                                >
                                        {devices.map(device => (
                                                <DeviceListItem key={device.id} device={device} onLocate={handleLocate} dragProps={dragProps(device)} />
                                        ))}
                                </div>
                        ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        {devices.map(device => (
                                                <DeviceCard key={device.id} device={device} onMoveDevice={onMoveDevice} onDeviceUpdate={onDeviceUpdate} onRenameDevice={onRenameDevice} onLocate={handleLocate} dragProps={dragProps(device)} />
                                        ))}
                                </div>
                        )}
                </section>
        );
};

export default RoomSection;
